"use client";

import { ChartLine as LineChart, MessageCircleHeart, RefreshCw } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Philosophy() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    icon: RefreshCw,
    title: "Seamless Data Integration",
    body: "We unify your disparate data sources into a single, coherent ecosystem. Our automated pipelines ensure your information is always current, accurate, and ready for analysis.",
    link: "Explore our automation",
    highlighted: false
  }, {
    icon: LineChart,
    title: "Actionable Intelligence",
    body: "Transform raw numbers into strategic assets. Our advanced analytics provide the clarity you need to make informed decisions that drive measurable business growth.",
    link: "View our analytics",
    highlighted: true
  }, {
    icon: MessageCircleHeart,
    title: "Strategic Partnership",
    body: "We act as an extension of your team, offering expert guidance and tailored consulting. We are committed to your long-term success through collaborative innovation.",
    link: "Meet our consultants",
    highlighted: false
  }];
  return <section data-section-id="7762" id="corephilosophy" className="text-foreground py-24 md:py-32 dark bg-accent">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div ref={intro.ref} className={`mx-auto max-w-[760px] text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-default font-normal text-foreground leading-[1.08]" style={{
          fontSize: "clamp(2.125rem, 3.5vw + 0.5rem, 3.5rem)",
          letterSpacing: "-0.02em"
        }}>
            Three core pillars that{" "}
            <span className="italic relative inline-block">
              drive excellence
              <span aria-hidden="true" className="absolute left-0 right-0 bg-primary" style={{
              bottom: "-4px",
              height: "2px"
            }} />
            </span>
            .
          </h2>
          <p className="mt-6 mx-auto max-w-[620px] text-muted-foreground font-default font-normal leading-[1.55]" style={{
          fontSize: "clamp(1.0625rem, 0.4vw + 1rem, 1.25rem)"
        }}>
            At Infinix Solutions, we combine technical precision with strategic insight to empower your digital transformation journey.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {features.map((feature, i) => {
          return <FeatureCard key={i} data-index={i} feature={feature} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function FeatureCard({
  feature,
  index,
  ...rest
}: {
  feature: {
    icon: React.ComponentType<{
      className?: string;
      strokeWidth?: number;
    }>;
    title: string;
    body: string;
    link: string;
    highlighted: boolean;
  };
  index: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = feature.icon;
  return <div ref={ref} className="transition-all duration-700 ease-out" style={{
    transitionDelay: `${index * 80}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(16px)"
  }} {...rest}>
      <Card className="group h-full bg-card text-card-foreground rounded-lg p-8 border-0 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-out flex flex-col gap-6">
        <div>
          {feature.highlighted ? <div className="inline-flex items-center justify-center rounded-lg" style={{
          width: "52px",
          height: "52px",
          backgroundColor: "hsl(199 89% 0% / 0.05)"
        }}>
              <Icon className="lucide lucide-chart-line lucide lucide-chart-line lucide lucide-chart-line lucide lucide-chart-line lucide lucide-chart-line lucide lucide-chart-line lucide lucide-chart-line lucide lucide-chart-line size-7 text-primary !opacity-[0.92] bg-primary-foreground" strokeWidth={1.75} />
            </div> : <Icon className="size-7 text-foreground" strokeWidth={1.75} />}
        </div>

        <h3 className="font-default font-semibold text-foreground leading-[1.25]" style={{
        fontSize: "clamp(1.25rem, 0.6vw + 1.1rem, 1.5rem)",
        letterSpacing: "-0.01em"
      }}>
          {feature.title}
        </h3>

        <p className="font-default text-muted-foreground leading-[1.6] flex-1">
          {feature.body}
        </p>

        <div className="mt-2">
          <button type="button" className="inline-flex items-center font-default font-medium underline decoration-1 underline-offset-4 hover:decoration-2 transition-all duration-200 text-secondary">
            {feature.link} →
          </button>
        </div>
      </Card>
    </div>;
}